import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { supabase } from '../services/supabaseClient';
import { useAuth } from './AuthContext';
import { useFirm } from './FirmContext';
import {
  DEFAULT_ITERATIONS,
  generateSaltB64u,
  deriveKey,
  buildVerificationToken,
  verifyKey,
  encryptString,
  decryptString,
  isEncryptedEnvelope,
} from '../components/messages/cryptoE2EE';

// ─── End-to-end encryption context ──────────────────────────────────────
//
// Two states relevant to the rest of the app:
//   - configured = true if the firm has run setup_firm_encryption (i.e.
//     firm_encryption row exists for this firm).
//   - unlocked   = true if the *current browser tab* has derived the key
//     and verified it against the firm's stored token.
//
// The derived AES-GCM CryptoKey is held only in memory (React state). It
// is intentionally not persisted to localStorage / sessionStorage: a
// stolen-laptop scenario should yield only ciphertext.
//
// Exposed actions:
//   setup(passphrase)        — firm-leader-only first-time setup. Generates
//                              a fresh salt, derives the key, builds a
//                              verification token, persists via RPC.
//   unlock(passphrase)       — re-derives the key, verifies vs. the stored
//                              token, retains the key in state.
//   rotate(passphrase)       — firm-leader-only. Generates new salt +
//                              token, replaces the firm_encryption row.
//   lock()                   — drops the key from memory.
//   encryptForChannel(text)  — returns the wire-format envelope.
//   decryptEnvelope(text)    — returns plaintext or null if locked /
//                              malformed / wrong key.

const EncryptionContext = createContext(null);

export function EncryptionProvider({ children }) {
  const { user } = useAuth();
  const { firmId } = useFirm();
  const [config, setConfig]     = useState(null);   // firm_encryption row or null
  const [key, setKey]           = useState(null);   // CryptoKey or null
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');

  // Load the firm's encryption config (or null if not set up yet).
  const refresh = useCallback(async () => {
    if (!firmId) { setConfig(null); setLoading(false); return; }
    setLoading(true);
    const { data } = await supabase
      .from('firm_encryption')
      .select('salt, iterations, verification_token, key_version, rotated_at, created_at')
      .eq('firm_id', firmId)
      .maybeSingle();
    setConfig(data || null);
    setLoading(false);
  }, [firmId]);

  useEffect(() => { refresh(); }, [refresh]);

  // Whenever the user logs out or the firm changes, drop the unlocked key.
  useEffect(() => {
    setKey(null);
  }, [user?.id, firmId]);

  const setup = useCallback(async (passphrase) => {
    setError('');
    if (!firmId) { setError('No firm context.'); return false; }
    if (!passphrase || passphrase.length < 12) {
      setError('Passphrase must be at least 12 characters.');
      return false;
    }
    if (config) {
      setError('Encryption is already configured — use Rotate to change the passphrase.');
      return false;
    }
    const salt = generateSaltB64u();
    const derived = await deriveKey(passphrase, salt, DEFAULT_ITERATIONS);
    const token   = await buildVerificationToken(derived);
    const { error: err } = await supabase.rpc('setup_firm_encryption', {
      p_salt:               salt,
      p_iterations:         DEFAULT_ITERATIONS,
      p_verification_token: token,
    });
    if (err) { setError(err.message || 'Setup failed.'); return false; }
    setKey(derived);
    await refresh();
    return true;
  }, [firmId, config, refresh]);

  const unlock = useCallback(async (passphrase) => {
    setError('');
    if (!config) { setError('Encryption is not configured for this firm yet.'); return false; }
    try {
      const derived = await deriveKey(passphrase, config.salt, config.iterations || DEFAULT_ITERATIONS);
      const ok = await verifyKey(derived, config.verification_token);
      if (!ok) { setError('Wrong passphrase.'); return false; }
      setKey(derived);
      return true;
    } catch (err) {
      setError(err.message || 'Unlock failed.');
      return false;
    }
  }, [config]);

  const rotate = useCallback(async (passphrase) => {
    setError('');
    if (!firmId) { setError('No firm context.'); return false; }
    if (!passphrase || passphrase.length < 12) {
      setError('New passphrase must be at least 12 characters.');
      return false;
    }
    const salt = generateSaltB64u();
    const derived = await deriveKey(passphrase, salt, DEFAULT_ITERATIONS);
    const token   = await buildVerificationToken(derived);
    const { error: err } = await supabase.rpc('rotate_firm_encryption', {
      p_salt:               salt,
      p_iterations:         DEFAULT_ITERATIONS,
      p_verification_token: token,
    });
    if (err) { setError(err.message || 'Rotation failed.'); return false; }
    setKey(derived);
    await refresh();
    return true;
  }, [firmId, refresh]);

  const lock = useCallback(() => { setKey(null); }, []);

  const encryptForChannel = useCallback(async (plaintext) => {
    if (!key) throw new Error('Encryption is locked.');
    return encryptString(plaintext ?? '', key);
  }, [key]);

  const decryptEnvelope = useCallback(async (envelope) => {
    if (!isEncryptedEnvelope(envelope)) return envelope;
    if (!key) return null;
    try {
      return await decryptString(envelope, key);
    } catch (_e) {
      return null;
    }
  }, [key]);

  const value = useMemo(() => ({
    loading,
    configured: !!config,
    unlocked:   !!key,
    error,
    keyVersion: config?.key_version ?? null,
    setup, unlock, rotate, lock,
    encryptForChannel, decryptEnvelope,
    refresh,
  }), [loading, config, key, error, setup, unlock, rotate, lock, encryptForChannel, decryptEnvelope, refresh]);

  return <EncryptionContext.Provider value={value}>{children}</EncryptionContext.Provider>;
}

export const useEncryption = () => useContext(EncryptionContext) || {
  loading: false, configured: false, unlocked: false, error: '',
  keyVersion: null,
  setup: async () => false, unlock: async () => false, rotate: async () => false,
  lock: () => {},
  encryptForChannel: async (s) => s,
  decryptEnvelope:   async (s) => s,
  refresh: () => {},
};
